import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'tattoo-studio-near-neapoli-exarchia';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

export default function TattooStudioNearNeapoliExarchia() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Νεάπολη: το studio είναι πρακτικά διπλανή πόρτα",
      "description": "Μένεις στη Νεάπολη; Το 210 Tattoo Athens είναι κυριολεκτικά δίπλα. Fine line, realistic και custom τατουάζ Αθήνα, ραντεβού μέσω Instagram.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-17",
      "dateModified": "2026-07-17",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-near-neapoli-article', articleSchema);
    return () => {
      document.getElementById('schema-near-neapoli-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ στη Νεάπολη Εξαρχείων | 210 Tattoo Athens</title>
        <meta name="description" content="Μένεις στη Νεάπολη; Το 210 Tattoo Athens είναι κυριολεκτικά δίπλα. Fine line, realistic και custom τατουάζ Αθήνα, ραντεβού μέσω Instagram." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ στη Νεάπολη Εξαρχείων | 210 Tattoo Athens" />
        <meta property="og:description" content="Το studio είναι πρακτικά διπλανή πόρτα: 5-8 λεπτά με τα πόδια από τη Νεάπολη." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ στη Νεάπολη Εξαρχείων | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Fine line, realistic και custom τατουάζ Αθήνα, στη γειτονιά δίπλα." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ στη Νεάπολη</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Νεάπολη:</span>
              <br />
              <span className="text-white">το studio είναι πρακτικά διπλανή πόρτα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">17 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Αν μένεις στη Νεάπολη, ξέρεις ήδη πόσο μικρή είναι η απόσταση μέχρι τη Γκύζη, οι δύο γειτονιές πρακτικά ενώνονται χωρίς αισθητό όριο. Το 210 Tattoo Athens, στην Αναστασίου Γενναδίου 34, βρίσκεται ουσιαστικά στη διπλανή γειτονιά της Νεάπολης, κάτι σπάνιο για όποιον ψάχνει τατουάζ Αθήνα και θέλει να μείνει κυριολεκτικά κοντά στο σπίτι του.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* How close */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο κοντά, <span className="text-pink-500">στην πράξη</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Μιλάμε για μια βόλτα 5 έως 8 λεπτών με τα πόδια από τα περισσότερα σημεία της Νεάπολης, ανάλογα από πού ξεκινάς. Δεν χρειάζεται λεωφορείο, δεν χρειάζεται σχεδιασμός διαδρομής, απλώς βγαίνεις από το σπίτι και σε λίγα λεπτά είσαι στο studio. Είναι η πιο κοντινή γειτονιά στη λίστα μας, και αυτό φαίνεται στο πόσο συχνά μας επισκέπτονται κάτοικοι της περιοχής, ακόμα και για κάτι μικρό ή αυθόρμητο, όπως ένα touch up ή ένα μικρό δεύτερο σχέδιο.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Οι περισσότεροι φοιτητές έχουν μαθήματα το πρωί, οπότε το ωράριό μας, 11:00 έως 20:00 τις καθημερινές, ταιριάζει άνετα με το πρόγραμμά τους. Δεν χρειάζεται να χάσεις μάθημα ή να κανονίσεις κάτι περίπλοκο, μπορείς να περάσεις το απόγευμα, μετά τα μαθήματα ή τη δουλειά.
            </p>
          </div>
        </section>

        {/* Who comes */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ποιοι έρχονται <span className="text-pink-500">από τη Νεάπολη</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η γειτονιά είναι γεμάτη φοιτητές, καλλιτέχνες και ανθρώπους κοντά στην Ανωτάτη Σχολή Καλών Τεχνών και το Πεδίον του Άρεως, οπότε δεν εκπλήσσει που αρκετά από τα αιτήματα που βλέπουμε είναι πιο πειραματικά ή καλλιτεχνικά στη σύλληψή τους. Έχουμε αρκετά <a href="/realistic-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">realistic τατουάζ</a> με πορτρέτα και λεπτομερείς αναφορές, αλλά και <a href="/sleeve-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">sleeve</a> projects που χτίζονται σε πολλές συνεδρίες γύρω από μία ενιαία ιδέα ή αφήγηση. Για κάτι πιο μικρό και πρώτο, το <a href="/fine-line-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">fine line τατουάζ</a> παραμένει πάντα δημοφιλής επιλογή, ειδικά ανάμεσα σε όσους θέλουν κάτι διακριτικό και καθαρό. Και όπως πάντα, ό,τι δεν χωράει πουθενά αλλού το σχεδιάζουμε ως <a href="/custom-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">custom</a>, από την αρχή, γύρω από τη δική σου ιδέα.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Δεν είναι σπάνιο να έρχονται δύο ή τρεις φίλοι μαζί από τη γειτονιά, ο ένας συνοδεύει τον άλλον στο πρώτο του ραντεβού και μετά κλείνει και ο ίδιος συνεδρία. Η μικρή απόσταση κάνει αυτό το σενάριο πολύ πιο εύκολο απ' ό,τι αν έπρεπε να κανονίσετε μια διαδρομή με το αυτοκίνητο ή το μετρό.
            </p>
          </div>
        </section>

        {/* First time */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πρώτη φορά; <span className="text-pink-500">Ξεκίνα από εδώ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Αν σκέφτεσαι το πρώτο σου τατουάζ και μένεις στη Νεάπολη, δες τον αναλυτικό οδηγό μας <Link to="/blog/first-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">το πρώτο σου τατουάζ στην Αθήνα</Link> πριν κλείσεις ραντεβού. Καλύπτει τι να περιμένεις, πώς να διαλέξεις σχέδιο και θέση, και πώς προετοιμάζεσαι σωστά πριν τη συνεδρία.
            </p>
          </div>
        </section>

        {/* After the appointment */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Και μετά <span className="text-pink-500">το ραντεβού</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Επειδή μένεις τόσο κοντά, η φροντίδα μετά το τατουάζ γίνεται πιο απλή, αν χρειαστεί κάτι στις πρώτες μέρες ή θες να περάσεις να δείξεις πώς επουλώνεται, η απόσταση δεν είναι εμπόδιο. Αυτό βοηθάει ιδιαίτερα σε μεγαλύτερα σχέδια ή σε projects πολλών συνεδριών, όπου η επικοινωνία ανάμεσα σε κάθε session μετράει.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πέρασε <span className="text-pink-500">μια βόλτα</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Δεν υπάρχει λόγος να ψάξεις τατουάζ στην Αθήνα μακριά από το σπίτι σου όταν το studio είναι κυριολεκτικά στη γειτονιά δίπλα. Είτε είναι το πρώτο σου τατουάζ είτε το τέταρτο, η μικρή απόσταση κάνει όλη τη διαδικασία, από το consultation μέχρι το ραντεβού, πιο εύκολη. Στείλε μήνυμα στο <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">Instagram</a> και κλείσε το ραντεβού σου.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να περιμένεις και πώς να προετοιμαστείς σωστά.</p>
              </Link>
              <Link to="/blog/tattoo-aftercare-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Φροντίδα Τατουάζ Μετά το Studio</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρεις οδηγίες aftercare για γρήγορη επούλωση.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/sleeve-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Sleeve Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
